/**
 * Blocurile care arata DATE REALE, nu ce a scris cineva in editor.
 *
 * Regula lor comuna, si motivul pentru care e scrisa aici o singura data: cand
 * nu exista date, blocul nu se randeaza. Un „Unde livram" gol s-ar citi ca „nu
 * livram nicaieri", iar un „Din meniu" gol ca „restaurantul nu are produse" —
 * amandoua neadevarate, si amandoua invizibile pentru cine administreaza piata.
 * Singura exceptie e lista de restaurante: acolo lipsa e o stare pe care
 * clientul chiar trebuie sa o vada, fiindca e tot motivul pentru care a intrat.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { blockFlag, blockNumber, blockText, formatRon, type OfferDto } from "@deliveryapp/shared";

import { fetchStorefront } from "../api/client";
import { formatAddressDetails } from "../components/AddressForm";
import { useBootstrap } from "../components/BootstrapProvider";
import { useCart } from "../components/CartProvider";
import { useCoverage } from "../components/CoverageProvider";
import { IconChevronRight, IconClose, IconLocate, IconPin, IconSearch } from "../components/icons";
import { ImageFallback, ResponsiveImage } from "../components/Media";
import { offerCustomerUnitPrice } from "../components/offer-merchandising";
import {
  EMPTY_STOREFRONT_FILTER,
  STOREFRONT_SORT_LABELS,
  collectTags,
  isStorefrontSort,
  matchesStorefrontFilter,
  onlyDeliverable,
  resolveStorefrontGate,
  sortStorefronts,
  type StorefrontFilter,
  type StorefrontSort,
} from "../components/storefront-listing";
import { SingleStorefrontCard, StorefrontPicker } from "../components/StorefrontPicker";
import { Button, Chip, Price } from "../components/ui";
import { useLoadable } from "../components/useLoadable";
import { columnClass, type BlockProps } from "./context";
import { BlockHeading, BlockLink, BlockSection } from "./parts";

// ---------------------------------------------------------------------------
// Adresa
// ---------------------------------------------------------------------------

/**
 * Bara de adresa: UN RAND, nu un formular. Cu adresa pusa, e confirmarea a ceea
 * ce s-a introdus, plus ce stim despre acoperire; fara ea, e invitatia — si in
 * ambele cazuri deschide aceeasi fereastra.
 */
export function AddressBarBlock({ block, ctx }: BlockProps) {
  const { session } = useCart();
  const { bootstrap } = useBootstrap();
  const coverage = useCoverage();
  const title = blockText(block, "title", ctx.t("home.address_title", "Unde livrăm?"));
  const hint = blockText(block, "hint", "Adaugă adresa ca să vezi dacă ajungem la tine");
  const address = session.address;

  const summary =
    address === null || coverage.state !== "ready" || coverage.deliverableCount === null
      ? null
      : bootstrap.storefronts.length === 1
        ? coverage.deliverableCount === 1
          ? "Livrăm la adresa ta"
          : "Momentan nu livrăm la adresa ta"
        : coverage.deliverableCount === 0
          ? "Niciun restaurant nu livrează la adresa ta"
          : `${coverage.deliverableCount} din ${bootstrap.storefronts.length} restaurante livrează la tine`;

  return (
    <button
      type="button"
      onClick={ctx.requestAddress}
      className="da-block-anchor da-card da-card-link da-tap flex w-full items-center gap-3 p-3.5 text-left"
      id={block.id}
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
        {address === null ? <IconLocate size={22} /> : <IconPin size={22} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-bold uppercase tracking-wide text-ink-muted">
          {address === null ? title : "Livrăm la"}
        </span>
        <span className="da-clamp-1 block text-base font-bold text-ink">{ctx.addressLabel ?? hint}</span>
        {address === null ? null : (
          <span className="da-clamp-1 block text-xs text-ink-muted">
            {[formatAddressDetails(address), summary].filter((part) => part !== null && part.length > 0).join(" · ")}
          </span>
        )}
      </span>
      <span className="shrink-0 rounded-control bg-brand px-3.5 py-2 text-sm font-bold text-brand-contrast">
        {address === null ? "Adaugă" : "Schimbă"}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Restaurante
// ---------------------------------------------------------------------------

/**
 * Cardul care tine locul listei pana la adresa: intrebarea de la care porneste
 * o platforma de livrare. Localitatile pietei sunt scrise sub el, ca cineva din
 * alt oras sa nu-si puna adresa degeaba.
 */
function AddressGateCard({ ctx, cities }: { ctx: BlockProps["ctx"]; cities: readonly string[] }) {
  const shown = cities.slice(0, 6);
  const rest = cities.length - shown.length;
  return (
    <div className="da-card flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:gap-5" data-testid="address-gate">
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
        <IconLocate size={26} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-lg font-bold text-ink">{ctx.t("home.address_title", "Unde livrăm?")}</p>
        <p className="text-sm text-ink-muted">Alege localitatea și strada, apoi îți arătăm doar restaurantele care ajung la tine.</p>
        {shown.length === 0 ? null : (
          <p className="mt-1 text-xs text-ink-muted">
            Livrăm în: {shown.join(", ")}
            {rest > 0 ? ` și încă ${rest}` : ""}
          </p>
        )}
      </div>
      <Button size="lg" onClick={ctx.requestAddress} trailing={<IconChevronRight size={18} />}>
        Adaugă adresa
      </Button>
    </div>
  );
}

function StorefrontCheckingRows({ count }: { count: number }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2" aria-busy="true" aria-label="Verificăm cine livrează la adresa ta">
      {Array.from({ length: Math.min(4, Math.max(1, count)) }).map((_, index) => (
        <li key={index} className="da-card overflow-hidden">
          <div className="da-shimmer aspect-video w-full" aria-hidden="true" />
          <div className="flex flex-col gap-2 p-3.5">
            <span className="da-shimmer block h-4 w-2/3 rounded" aria-hidden="true" />
            <span className="da-shimmer block h-3 w-1/2 rounded" aria-hidden="true" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function StorefrontGridBlock({ block, ctx, headingLevel }: BlockProps) {
  const { bootstrap } = useBootstrap();
  const { session } = useCart();
  const coverage = useCoverage();
  const [filter, setFilter] = useState<StorefrontFilter>(EMPTY_STOREFRONT_FILTER);
  const [sort, setSort] = useState<StorefrontSort | null>(null);
  const [showAll, setShowAll] = useState(false);

  const limit = blockNumber(block, "limit", 0);
  const configuredSort = blockText(block, "sortBy", "recommended");
  const activeSort: StorefrontSort = sort ?? (isStorefrontSort(configuredSort) ? configuredSort : "recommended");
  const showSearch = blockFlag(block, "showSearch", false);
  const showFilters = blockFlag(block, "showFilters", false);
  // Regula produsului: restaurantele se vad DUPA adresa si doar cele care
  // livreaza la ea. Ambele se pot opri din builder, pentru piete care vor sa se
  // rasfoiasca inainte (vechiul comportament).
  const requireAddress = blockFlag(block, "requireAddress", true);
  const hideUndeliverable = blockFlag(block, "hideUndeliverable", true);
  const all = bootstrap.storefronts;
  const single = all.length === 1;
  const tags = useMemo(() => (showFilters ? collectTags(all) : []), [all, showFilters]);
  const hasAddress = session.address !== null;
  const addressKey = session.address === null ? null : `${session.address.lat},${session.address.lng}`;

  // „Vezi toate restaurantele" e o alegere pentru ADRESA curenta: la o adresa
  // noua lista revine la cele care livreaza acolo.
  useEffect(() => {
    setShowAll(false);
  }, [addressKey]);

  const gate = resolveStorefrontGate({
    hasAddress,
    coverageState: coverage.state,
    requireAddress,
    hideUndeliverable,
    showAll,
  });
  const deliverableOnlyGate = gate.kind === "list" && gate.filter === "deliverable";

  const visible = useMemo(() => {
    const base = deliverableOnlyGate ? onlyDeliverable(all, coverage.entryFor) : all;
    const filtered = base.filter((storefront) => matchesStorefrontFilter(storefront, filter, coverage.entryFor(storefront.slug)));
    const sorted = sortStorefronts(filtered, activeSort, coverage.entryFor);
    return limit > 0 ? sorted.slice(0, limit) : sorted;
  }, [all, deliverableOnlyGate, filter, activeSort, coverage, limit]);

  // Cu o singura vitrina nu e nimic de ales: titlul de grup dispare, cardul
  // devine o usa mare spre meniu. Cu zero vitrine sectiunea RAMANE, cu mesaj:
  // e tinta ancorei `#vitrine` din sabloane si singura explicatie a clientului.
  const titled = !single || all.length === 0;
  const filtering = filter.search.trim().length > 0 || filter.tag !== null || filter.deliverableOnly;
  const noneDeliverable = deliverableOnlyGate && !filtering && visible.length === 0 && all.length > 0;

  return (
    <BlockSection id={block.id} labelledBy={titled ? `bloc-${block.id}` : undefined}>
      {titled ? (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <BlockHeading
            id={`bloc-${block.id}`}
            title={blockText(block, "title", ctx.t("home.storefronts_title", "De unde comanzi"))}
            subtitle={blockText(block, "subtitle")}
            level={headingLevel}
          />
          {all.length < 3 || gate.kind !== "list" ? null : (
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              <span className="sr-only sm:not-sr-only">Ordine</span>
              <select
                className="rounded-control border border-line bg-canvas px-2.5 py-2 text-sm font-semibold text-ink"
                value={activeSort}
                onChange={(event) => setSort(isStorefrontSort(event.target.value) ? event.target.value : "recommended")}
              >
                {(Object.keys(STOREFRONT_SORT_LABELS) as StorefrontSort[]).map((key) => (
                  <option key={key} value={key}>
                    {STOREFRONT_SORT_LABELS[key]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      ) : null}

      {gate.kind !== "list" || single || (!showSearch && tags.length === 0 && coverage.state !== "ready") ? null : (
        <div className="flex flex-col gap-2">
          {showSearch ? (
            <div className="relative max-w-md">
              <label htmlFor={`cauta-${block.id}`} className="sr-only">
                Caută un restaurant
              </label>
              <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-ink-muted">
                <IconSearch size={20} />
              </span>
              <input
                id={`cauta-${block.id}`}
                type="search"
                value={filter.search}
                onChange={(event) => setFilter({ ...filter, search: event.target.value })}
                placeholder="Caută restaurant sau bucătărie"
                className="w-full rounded-full border border-line bg-canvas py-3 pl-11 pr-10 text-base text-ink placeholder:text-ink-muted focus:border-brand"
              />
              {filter.search.length === 0 ? null : (
                <button
                  type="button"
                  onClick={() => setFilter({ ...filter, search: "" })}
                  aria-label="Șterge căutarea"
                  className="absolute inset-y-0 right-2 grid w-9 place-items-center text-ink-muted hover:text-ink"
                >
                  <IconClose size={18} />
                </button>
              )}
            </div>
          ) : null}
          {tags.length === 0 && coverage.state !== "ready" ? null : (
            <div className="da-rail da-rail-bleed py-0.5" role="group" aria-label="Filtre pentru restaurante">
              {coverage.state !== "ready" || deliverableOnlyGate ? null : (
                <Chip selected={filter.deliverableOnly} onClick={() => setFilter({ ...filter, deliverableOnly: !filter.deliverableOnly })}>
                  <IconPin size={14} /> Livrează la mine
                </Chip>
              )}
              {tags.map((tag) => (
                <Chip key={tag} selected={filter.tag === tag} onClick={() => setFilter({ ...filter, tag: filter.tag === tag ? null : tag })}>
                  {tag}
                </Chip>
              ))}
            </div>
          )}
        </div>
      )}

      {gate.kind === "ask_address" && all.length > 0 ? (
        <AddressGateCard ctx={ctx} cities={bootstrap.cities ?? []} />
      ) : gate.kind === "checking" ? (
        <StorefrontCheckingRows count={all.length} />
      ) : noneDeliverable ? (
        <div className="da-card flex flex-col gap-3 p-5" data-testid="no-deliverable">
          <p className="text-base font-bold text-ink">Niciun restaurant nu livrează la adresa ta acum.</p>
          <p className="text-sm text-ink-muted">
            {coverage.deliverableCount === 0 && all.some((storefront) => coverage.entryFor(storefront.slug)?.status === "no_vendor")
              ? "Zona ta e acoperită, dar bucătăriile care ajung aici nu primesc comenzi în acest moment. Poți încerca mai târziu."
              : "Verifică adresa sau alege alta — poate strada e chiar la marginea zonei."}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={ctx.requestAddress}>Schimbă adresa</Button>
            {hideUndeliverable ? (
              <Button variant="secondary" onClick={() => setShowAll(true)}>
                Vezi toate restaurantele
              </Button>
            ) : null}
          </div>
        </div>
      ) : single && all[0] !== undefined ? (
        <SingleStorefrontCard
          storefront={all[0]}
          coverage={coverage.entryFor(all[0].slug)}
          coverageState={coverage.state}
          onSelect={ctx.openStorefront}
        />
      ) : visible.length === 0 && filtering ? (
        <div className="rounded-card border border-line bg-surface p-4 text-sm text-ink">
          <p className="font-semibold">Niciun restaurant pentru selecția ta.</p>
          <Button className="mt-2" size="sm" variant="secondary" onClick={() => setFilter(EMPTY_STOREFRONT_FILTER)}>
            Vezi toate restaurantele
          </Button>
        </div>
      ) : (
        <StorefrontPicker
          storefronts={visible}
          coverageFor={coverage.entryFor}
          coverageState={coverage.state}
          onSelect={ctx.openStorefront}
          layout={blockText(block, "layout", "grila")}
          columns={blockText(block, "columns", "2")}
        />
      )}

      {ctx.addressLabel !== null || all.length === 0 || requireAddress ? null : (
        // UN SINGUR rand pentru toata lista, nu unul pe card: pe o piata cu 12
        // vitrine, acelasi text si acelasi buton s-ar fi repetat de 12 ori.
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-muted">
          <span>Nu știm încă dacă livrăm la tine.</span>
          <button type="button" onClick={ctx.requestAddress} className="da-tap rounded-control font-bold text-brand-on-surface underline">
            Adaugă adresa
          </button>
        </p>
      )}
      {gate.kind === "list" && gate.filter === "all" && hideUndeliverable && hasAddress && coverage.state === "ready" && showAll ? (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-muted">
          <span>Restaurantele care nu ajung la tine sunt marcate.</span>
          <button type="button" onClick={() => setShowAll(false)} className="da-tap rounded-control font-bold text-brand-on-surface underline">
            Arată doar cine livrează
          </button>
        </p>
      ) : null}
      {coverage.truncated ? (
        <p className="text-sm text-ink-muted">Acoperirea s-a verificat doar pentru primele restaurante; pentru restul o afli în meniu.</p>
      ) : null}
    </BlockSection>
  );
}

// ---------------------------------------------------------------------------
// Din meniu
// ---------------------------------------------------------------------------

/**
 * Cateva produse reale din meniul publicat.
 *
 * Cardul duce la vitrina, nu adauga in cos. Deliberat: un produs poate avea
 * grupuri de optiuni obligatorii (ADR-024), iar un „+" care ar sari peste ele ar
 * trimite la bucatarie o comanda pe care nimeni nu a configurat-o.
 */
export function MenuHighlightsBlock({ block, ctx, headingLevel }: BlockProps) {
  const { bootstrap, marketKey } = useBootstrap();
  const { session } = useCart();
  // Aceeasi poarta ca lista de restaurante: fara adresa, un card de produs e o
  // usa spre un meniu din care clientul afla la cos ca nu se livreaza la el.
  // Blocul se ascunde (cardul „Unde livram?" e deja in lista de restaurante).
  const requireAddress = blockFlag(block, "requireAddress", true);
  const gated = requireAddress && session.address === null;
  const wanted = blockText(block, "storefrontSlug").trim().toLowerCase();
  const storefront = useMemo(() => {
    if (wanted.length > 0) {
      return bootstrap.storefronts.find((item) => item.slug.toLowerCase() === wanted) ?? null;
    }
    return bootstrap.storefronts[0] ?? null;
  }, [bootstrap.storefronts, wanted]);

  const slug = storefront?.slug ?? null;
  const loaded = useLoadable(
    (signal) => (slug === null || gated ? Promise.resolve(null) : fetchStorefront(marketKey, slug, signal)),
    [marketKey, slug, gated],
  );

  const category = blockText(block, "category").trim().toLowerCase();
  const limit = Math.max(1, Math.min(24, blockNumber(block, "limit", 6)));

  const offers: OfferDto[] = useMemo(() => {
    const menus = loaded.data?.menus ?? [];
    const all = menus.flatMap((menu) => menu.offers);
    const usable = all.filter((offer) => offer.available);
    const filtered =
      category.length === 0 ? usable : usable.filter((offer) => offer.categoryPath.toLowerCase().includes(category));
    // Produsele cu poza intai: un bloc de „recomandari" fara nicio poza pe
    // primul rand nu recomanda nimic. Ordinea din meniu se pastreaza in rest.
    return [...filtered]
      .sort((a, b) => Number(b.images.length > 0) - Number(a.images.length > 0) || a.sortOrder - b.sortOrder)
      .slice(0, limit);
  }, [loaded.data, category, limit]);

  if (slug === null || gated) return null;
  if (loaded.state === "loading") {
    return (
      <BlockSection id={block.id}>
        <BlockHeading title={blockText(block, "title")} level={headingLevel} />
        <div className={`grid gap-3 ${columnClass(blockText(block, "columns", "3"))}`} aria-hidden="true">
          {[0, 1, 2].map((index) => (
            <span key={index} className="da-shimmer block h-56 rounded-card" />
          ))}
        </div>
      </BlockSection>
    );
  }
  if (loaded.state === "error" || offers.length === 0) return null;

  const title = blockText(block, "title");
  return (
    <BlockSection id={block.id} labelledBy={title.length > 0 ? `bloc-${block.id}` : undefined}>
      <BlockHeading id={`bloc-${block.id}`} title={title} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <ul className={`grid gap-3 ${columnClass(blockText(block, "columns", "3"))}`}>
        {offers.map((offer, index) => (
          <li key={offer.offerId} className="da-card da-card-link overflow-hidden">
            <Link
              // FARA `onClick` care sa navigheze: `Link` navigheaza deja, iar
              // pagina vitrinei isi selecteaza singura vitrina din slug.
              to={`/vitrina/${encodeURIComponent(slug)}`}
              className="da-tap flex h-full flex-col text-left"
            >
              <ResponsiveImage
                src={offer.images[0]?.url ?? offer.imageUrl}
                alt=""
                ratio="4/3"
                rounded={false}
                priority={index === 0}
                sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw"
                fallback={<ImageFallback />}
              />
              <span className="flex flex-1 flex-col gap-1 p-3.5">
                <span className="text-base font-bold text-ink">{offer.name}</span>
                {offer.description === null || offer.description === undefined || offer.description.length === 0 ? null : (
                  <span className="da-clamp-2 text-sm text-ink-muted">{offer.description}</span>
                )}
                <span className="mt-auto pt-2 text-base font-extrabold text-ink">
                  <Price amount={offerCustomerUnitPrice(offer)} />
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div>
        <BlockLink label={blockText(block, "ctaLabel", "Vezi tot meniul")} href={`/vitrina/${encodeURIComponent(slug)}`} variant="secondary">
          <IconChevronRight size={18} />
        </BlockLink>
      </div>
    </BlockSection>
  );
}

// ---------------------------------------------------------------------------
// Unde livram
// ---------------------------------------------------------------------------

export function CoverageListBlock({ block, headingLevel }: BlockProps) {
  const { bootstrap } = useBootstrap();
  const areas = bootstrap.coverage ?? [];
  const showFees = blockFlag(block, "showFees", true);
  const showMinOrder = blockFlag(block, "showMinOrder", false);
  const grouped = blockFlag(block, "groupByCity", true);

  const byCity = useMemo(() => {
    const map = new Map<string, typeof areas>();
    for (const area of areas) {
      const key = area.city.trim().length > 0 ? area.city : "Alte zone";
      const bucket = map.get(key);
      if (bucket === undefined) map.set(key, [area]);
      else bucket.push(area);
    }
    return [...map.entries()];
  }, [areas]);

  if (areas.length === 0) return null;

  const describe = (area: (typeof areas)[number]): string => {
    const parts: string[] = [];
    if (showFees) {
      parts.push(area.deliveryFee === 0 ? "livrare gratuită" : `livrare ${formatRon(area.deliveryFee)}`);
    }
    if (showMinOrder && area.minOrderValue > 0) parts.push(`minim ${formatRon(area.minOrderValue)}`);
    if (showFees && area.freeDeliveryThreshold !== null && area.freeDeliveryThreshold !== undefined && area.freeDeliveryThreshold > 0) {
      parts.push(`gratuit peste ${formatRon(area.freeDeliveryThreshold)}`);
    }
    return parts.join(" · ");
  };

  return (
    <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
      <BlockHeading id={`bloc-${block.id}`} title={blockText(block, "title", "Unde livrăm")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      {grouped ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {byCity.map(([city, list]) => (
            <div key={city} className="da-card flex flex-col gap-2 p-4">
              <p className="text-base font-bold text-ink">{city}</p>
              <ul className="flex flex-col gap-1 text-sm text-ink-muted">
                {list.map((area) => (
                  <li key={`${city}-${area.name}`} className="flex flex-wrap justify-between gap-x-3">
                    <span className="text-ink">{area.name}</span>
                    <span>{describe(area)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {areas.map((area) => (
            <li key={`${area.city}-${area.name}`} className="rounded-full border border-line bg-surface px-3 py-1.5 text-sm text-ink">
              <span className="font-semibold">{area.name}</span>
              <span className="text-ink-muted">{describe(area).length > 0 ? ` · ${describe(area)}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
      {bootstrap.coverageTruncated !== true ? null : (
        <p className="text-sm text-ink-muted">Livrăm și în alte zone decât cele de mai sus. Adaugă adresa ca să vezi dacă ajungem la tine.</p>
      )}
    </BlockSection>
  );
}

// ---------------------------------------------------------------------------
// Program
// ---------------------------------------------------------------------------

const WEEK_DAYS: Array<{ key: string; label: string }> = [
  { key: "monday", label: "Luni" },
  { key: "tuesday", label: "Marți" },
  { key: "wednesday", label: "Miercuri" },
  { key: "thursday", label: "Joi" },
  { key: "friday", label: "Vineri" },
  { key: "saturday", label: "Sâmbătă" },
  { key: "sunday", label: "Duminică" },
];

export function HoursBlock({ block, headingLevel }: BlockProps) {
  const { config } = useBootstrap();
  const weekly = config.hours.weekly;
  const exceptions = config.hours.exceptions ?? [];
  if (weekly === undefined && exceptions.length === 0) return null;
  // The schedule belongs to the restaurant's timezone, not the visitor's.
  let parts: Intl.DateTimeFormatPart[];
  try { parts = new Intl.DateTimeFormat("en-CA", { timeZone: config.hours.timezone, year: "numeric", month: "2-digit", day: "2-digit", weekday: "long" }).formatToParts(new Date()); }
  catch { parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", weekday: "long" }).formatToParts(new Date()); }
  const part = (type: string) => parts.find(item => item.type === type)?.value ?? "";
  const todayDate = `${part("year")}-${part("month")}-${part("day")}`;
  const todayKey = part("weekday").toLowerCase();
  const specialToday = exceptions.find(item => item.date === todayDate);
  const upcoming = exceptions.filter(item => item.date >= todayDate).sort((a, b) => a.date.localeCompare(b.date));
  const rows = WEEK_DAYS.map(day => {
    const windows = weekly?.[day.key] ?? weekly?.[day.label.toLowerCase()] ?? [];
    const today = day.key === todayKey;
    const special = today && specialToday;
    return {
      label: day.label,
      today,
      text: special ? special.closed ? "Închis · program special" : `${special.from ?? ""}–${special.to ?? ""} · program special`
        : windows.length === 0 ? "Închis" : windows.map((window) => `${window.from}–${window.to}`).join(", "),
    };
  });

  return (
    <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
      <BlockHeading id={`bloc-${block.id}`} title={blockText(block, "title", "Program")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <dl className="da-card max-w-readable divide-y divide-line">
        {(weekly ? rows : []).map((row) => (
          <div key={row.label} className={`flex items-center justify-between gap-4 px-4 py-2.5 text-sm ${row.today ? "bg-brand-soft" : ""}`}>
            <dt className={row.today ? "font-bold text-ink" : "text-ink"}>
              {row.label}
              {row.today ? <span className="ml-2 text-xs font-semibold uppercase text-brand-on-surface">azi</span> : null}
            </dt>
            <dd className={row.text === "Închis" ? "text-ink-muted" : "font-semibold text-ink"}>{row.text}</dd>
          </div>
        ))}
      </dl>
      {upcoming.length > 0 ? <div className="max-w-readable rounded-card border border-line p-4">
        <h3 className="text-sm font-bold text-ink">Program special</h3>
        <dl className="mt-2 space-y-2 text-sm">{upcoming.map((item, index) => <div key={`${item.date}-${index}`} className="flex flex-wrap justify-between gap-2"><dt><time dateTime={item.date}>{item.date.split("-").reverse().join(".")}</time></dt><dd className="font-semibold">{item.closed ? "Închis" : `${item.from ?? ""}–${item.to ?? ""}`}</dd></div>)}</dl>
      </div> : null}
      {blockText(block, "note").length === 0 ? null : <p className="text-sm text-ink-muted">{blockText(block, "note")}</p>}
    </BlockSection>
  );
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export function ContactBlock({ block, headingLevel }: BlockProps) {
  const { config } = useBootstrap();
  const phone = blockFlag(block, "showPhone", true) ? (config.identity.supportPhone ?? "") : "";
  const email = blockFlag(block, "showEmail", true) ? (config.identity.supportEmail ?? "") : "";
  const address = blockText(block, "address");
  const mapUrl = blockText(block, "mapUrl");

  if (phone.length === 0 && email.length === 0 && address.length === 0) return null;

  return (
    <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
      <BlockHeading id={`bloc-${block.id}`} title={blockText(block, "title", "Contact")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <ul className="flex flex-col gap-2 text-base text-ink">
        {phone.length === 0 ? null : (
          <li>
            <a className="font-semibold underline" href={`tel:${phone.replace(/\s+/g, "")}`}>
              {phone}
            </a>
          </li>
        )}
        {email.length === 0 ? null : (
          <li>
            <a className="underline" href={`mailto:${email}`}>
              {email}
            </a>
          </li>
        )}
        {address.length === 0 ? null : <li className="text-ink-muted">{address}</li>}
      </ul>
      <div>
        <BlockLink label={mapUrl.length > 0 ? "Deschide harta" : ""} href={mapUrl} variant="secondary" />
      </div>
    </BlockSection>
  );
}

// ---------------------------------------------------------------------------
// Plata
// ---------------------------------------------------------------------------

const PAYMENT_LABELS: Record<string, string> = {
  cash_on_delivery: "Numerar la livrare",
};

export function PaymentNoteBlock({ block, headingLevel }: BlockProps) {
  const { config } = useBootstrap();
  const named = config.payments.methods
    .map((method) => PAYMENT_LABELS[method])
    .filter((label): label is string => label !== undefined);
  if (named.length === 0) return null;

  const title = blockText(block, "title");
  return (
    <BlockSection id={block.id}>
      <BlockHeading title={title} level={headingLevel} />
      <p className="text-sm text-ink-muted">{`Plată acceptată: ${named.join(", ")}. Bonul fiscal îl primești de la curier, odată cu comanda.`}</p>
    </BlockSection>
  );
}
